import { getFallbackTimeoutMs } from './transferConfig';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    Database,
    ref,
    onValue,
    set,
    push,
    runTransaction,
    serverTimestamp,
    Unsubscribe,
} from 'firebase/database';
import firebaseService from './firebaseService';
import { log, error } from '../Utils/logger';

export type SignalingRole = 'caller' | 'callee';
export type CandidatePathMode = 'split' | 'legacy';

type DescriptionEnvelope = {
    attemptId: string;
} & RTCSessionDescriptionInit;

type CandidateEnvelope = { attemptId: string } & RTCIceCandidateInit;

export type ReconnectRequest = {
    id: string;
    attemptId: string;
};

export type ServerManifest = {
    metadata: string;
    role: SignalingRole;
    status: 'uploading' | 'ready' | 'received' | 'cancelled' | 'failed';
    expiresAt: number;
};
export type ServerState = {
    enabled?: boolean;
    emulateSlow?: boolean;
    present?: Partial<Record<SignalingRole, boolean>>;
    transfers?: Record<string, ServerManifest>;
};

export class Signaling {
    public serverState = new BehaviorSubject<ServerState>({});
    public fallbackReason = new BehaviorSubject<string | null>(null);
    private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    private connected = false;
    public get id() {
        return this.sessionId;
    }
    public get localRole() {
        return this.role;
    }
    public get serverEnabled() {
        return this.serverState.value.enabled === true;
    }
    public get emulateSlow() {
        return (
            new URLSearchParams(window.location.search).get('server_only') ===
                '1' || this.serverState.value.emulateSlow === true
        );
    }

    public async announcePresence() {
        await firebaseService.initialize();
        if (this.destroyed) return;
        if (this.emulateSlow)
            await set(
                ref(this.db, `files/${this.sessionId}/server/emulateSlow`),
                true,
            );
        await set(
            ref(this.db, `files/${this.sessionId}/server/present/${this.role}`),
            true,
        );
        if (this.role === 'callee') this.startFallbackTimer();
    }

    private startFallbackTimer() {
        if (
            this.connected ||
            this.serverEnabled ||
            this.fallbackTimer ||
            this.fallbackReason.value
        )
            return;
        this.fallbackTimer = setTimeout(() => {
            this.fallbackTimer = null;
            this.suggestServer('slow_connection');
        }, getFallbackTimeoutMs());
    }

    public suggestServer(reason: string) {
        if (!this.destroyed && !this.connected && !this.serverEnabled) {
            this.fallbackReason.next(reason);
        }
    }

    public markConnected() {
        this.connected = true;
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
        this.fallbackReason.next(null);
    }

    public markDisconnected() {
        this.connected = false;
        this.startFallbackTimer();
    }

    public async selectServer() {
        await firebaseService.initialize();
        // A monotonic shared switch: either endpoint can opt in for both.
        await set(ref(this.db, `files/${this.sessionId}/server/enabled`), true);
    }

    private db: Database;
    private sessionId: string;
    private role: SignalingRole;
    private candidatePathMode: CandidatePathMode;
    private localCandidatesPath: string;
    private remoteCandidatesPath: string;

    private offerSubject =
        new BehaviorSubject<RTCSessionDescriptionInit | null>(null);
    private answerSubject =
        new BehaviorSubject<RTCSessionDescriptionInit | null>(null);
    private iceCandidatesSubject = new BehaviorSubject<RTCIceCandidate[]>([]);
    private filesSubject = new BehaviorSubject<any[]>([]);
    private reconnectRequestSubject =
        new BehaviorSubject<ReconnectRequest | null>(null);

    public offer$: Observable<RTCSessionDescriptionInit | null> =
        this.offerSubject.asObservable();
    public answer$: Observable<RTCSessionDescriptionInit | null> =
        this.answerSubject.asObservable();
    public iceCandidates$: Observable<RTCIceCandidate[]> =
        this.iceCandidatesSubject.asObservable();
    public files$: Observable<any[]> = this.filesSubject.asObservable();
    public reconnectRequest$: Observable<ReconnectRequest | null> =
        this.reconnectRequestSubject.asObservable();

    private unsubscribes: Unsubscribe[] = [];
    private destroyed = false;
    private activeAttemptId: string | null = null;
    private rawRemoteCandidates: unknown[] = [];

    constructor(
        sessionId: string,
        role: SignalingRole,
        candidatePathMode: CandidatePathMode = 'split',
    ) {
        this.sessionId = sessionId;
        this.role = role;
        this.candidatePathMode = candidatePathMode;
        if (candidatePathMode === 'legacy') {
            this.localCandidatesPath = 'iceCandidates';
            this.remoteCandidatesPath = 'iceCandidates';
        } else {
            this.localCandidatesPath =
                role === 'caller' ? 'callerCandidates' : 'calleeCandidates';
            this.remoteCandidatesPath =
                role === 'caller' ? 'calleeCandidates' : 'callerCandidates';
        }
        this.db = firebaseService.getDatabase();
        firebaseService.initialize().then(() => {
            if (!this.destroyed) {
                this.initializeListeners();
            }
        });
    }

    private initializeListeners() {
        this.unsubscribes.push(
            onValue(
                ref(this.db, `files/${this.sessionId}/server`),
                (snapshot) => {
                    const state = snapshot.val() || {};
                    // Once selected, reconnects or stale snapshots cannot revert transport.
                    if (this.serverEnabled) state.enabled = true;
                    this.serverState.next(state);
                    if (state.enabled === true) this.markConnected();
                    else if (state.present?.callee) this.startFallbackTimer();
                },
                () => this.suggestServer('signaling_error'),
            ),
        );
        const refs = {
            offer: ref(this.db, `files/${this.sessionId}/offer`),
            answer: ref(this.db, `files/${this.sessionId}/answer`),
            remoteCandidates: ref(
                this.db,
                `files/${this.sessionId}/${this.remoteCandidatesPath}`,
            ),
            files: ref(this.db, `files/${this.sessionId}/files`),
            reconnectRequest: ref(
                this.db,
                `files/${this.sessionId}/reconnectRequest`,
            ),
        };

        const handleListenerError = (
            key: keyof typeof refs,
            errValue: unknown,
        ) => {
            const message =
                errValue instanceof Error
                    ? errValue.message
                    : String(errValue ?? 'unknown');

            error(`Signaling: Listener error (${key})`, {
                sessionId: this.sessionId,
                message,
            });

            void firebaseService.logEvent('signaling_listener_error', {
                session_id: this.sessionId,
                ref: key,
                message,
            });
        };

        this.unsubscribes.push(
            onValue(
                refs.offer,
                (snapshot) => {
                    const val = snapshot.val();
                    const envelope = this.readDescriptionEnvelope(val);
                    if (envelope) {
                        this.activeAttemptId = envelope.attemptId;
                        this.offerSubject.next({
                            type: envelope.type,
                            sdp: envelope.sdp,
                        });
                        this.publishRemoteCandidates();
                    } else {
                        this.offerSubject.next(val ?? null);
                    }
                },
                (errValue) => handleListenerError('offer', errValue),
            ),
            onValue(
                refs.answer,
                (snapshot) => {
                    const val = snapshot.val();
                    const envelope = this.readDescriptionEnvelope(val);
                    if (
                        envelope &&
                        envelope.attemptId === this.activeAttemptId
                    ) {
                        this.answerSubject.next({
                            type: envelope.type,
                            sdp: envelope.sdp,
                        });
                    } else if (!envelope) {
                        this.answerSubject.next(val ?? null);
                    }
                },
                (errValue) => handleListenerError('answer', errValue),
            ),
            onValue(
                refs.remoteCandidates,
                (snapshot) => {
                    const val = snapshot.val();
                    this.rawRemoteCandidates = val ? Object.values(val) : [];
                    this.publishRemoteCandidates();
                },
                (errValue) => handleListenerError('remoteCandidates', errValue),
            ),
            onValue(
                refs.files,
                (snapshot) => {
                    const val = snapshot.val();
                    const files = val ? Object.values(val) : [];
                    this.filesSubject.next(files);
                },
                (errValue) => handleListenerError('files', errValue),
            ),
            onValue(
                refs.reconnectRequest,
                (snapshot) => {
                    const val = snapshot.val();
                    if (
                        val &&
                        typeof val.id === 'string' &&
                        typeof val.attemptId === 'string'
                    ) {
                        this.reconnectRequestSubject.next({
                            id: val.id,
                            attemptId: val.attemptId,
                        });
                    }
                },
                (errValue) => handleListenerError('reconnectRequest', errValue),
            ),
        );
    }

    public async ensureSessionCreated() {
        await firebaseService.initialize();
        await runTransaction(
            ref(this.db, `files/${this.sessionId}/createdAt`),
            (currentValue) => currentValue ?? serverTimestamp(),
            { applyLocally: false },
        );
    }

    public beginAttempt(): string {
        const attemptId = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        this.activeAttemptId = attemptId;
        this.rawRemoteCandidates = [];
        this.iceCandidatesSubject.next([]);
        this.answerSubject.next(null);
        return attemptId;
    }

    public async setOffer(
        offer: RTCSessionDescriptionInit,
        attemptId = this.activeAttemptId ?? this.beginAttempt(),
    ) {
        await firebaseService.initialize();
        if (this.serverEnabled || this.emulateSlow || this.destroyed) return;
        const envelope: DescriptionEnvelope = { ...offer, attemptId };
        await set(ref(this.db, `files/${this.sessionId}/offer`), envelope);
    }

    public async setAnswer(answer: RTCSessionDescriptionInit) {
        await firebaseService.initialize();
        if (this.serverEnabled || this.emulateSlow || this.destroyed) return;
        const attemptId = this.activeAttemptId;
        if (!attemptId) {
            throw new Error('Cannot set an answer without an active attempt');
        }
        const envelope: DescriptionEnvelope = { ...answer, attemptId };
        await set(ref(this.db, `files/${this.sessionId}/answer`), envelope);
    }

    public async addIceCandidate(candidate: RTCIceCandidate) {
        await firebaseService.initialize();
        // Use toJSON if available for better compatibility across browsers
        const plainCandidate =
            typeof candidate.toJSON === 'function'
                ? candidate.toJSON()
                : JSON.parse(JSON.stringify(candidate));
        if (this.serverEnabled || this.emulateSlow || this.destroyed) return;
        const attemptId = this.activeAttemptId;
        if (!attemptId) {
            error('Signaling: Ignoring ICE candidate without active attempt');
            return;
        }
        const envelope: CandidateEnvelope = { ...plainCandidate, attemptId };
        await push(
            ref(this.db, `files/${this.sessionId}/${this.localCandidatesPath}`),
            envelope,
        );
    }

    public async requestReconnect() {
        await firebaseService.initialize();
        if (!this.activeAttemptId || this.serverEnabled) return;
        await set(ref(this.db, `files/${this.sessionId}/reconnectRequest`), {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`,
            attemptId: this.activeAttemptId,
            requestedAt: serverTimestamp(),
        });
    }

    public getActiveAttemptId() {
        return this.activeAttemptId;
    }

    public async registerFile(metadata: any) {
        await firebaseService.initialize();
        await push(ref(this.db, `files/${this.sessionId}/files`), metadata);
    }

    public async clear() {
        await firebaseService.initialize();
        await set(ref(this.db, `files/${this.sessionId}`), null);
    }

    public destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        this.unsubscribes.forEach((unsub) => unsub());
        this.offerSubject.complete();
        this.answerSubject.complete();
        this.iceCandidatesSubject.complete();
        this.filesSubject.complete();
        this.reconnectRequestSubject.complete();
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
        this.serverState.complete();
        this.fallbackReason.complete();
        log('Signaling: Destroyed listeners for session', this.sessionId);
    }

    public isDestroyed() {
        return this.destroyed;
    }

    private readDescriptionEnvelope(
        value: unknown,
    ): DescriptionEnvelope | null {
        if (!value || typeof value !== 'object') return null;
        const candidate = value as Partial<DescriptionEnvelope>;
        if (
            typeof candidate.attemptId !== 'string' ||
            (candidate.type !== 'offer' && candidate.type !== 'answer')
        ) {
            return null;
        }
        return candidate as DescriptionEnvelope;
    }

    private publishRemoteCandidates() {
        const candidates = this.rawRemoteCandidates.flatMap((value) => {
            if (!value || typeof value !== 'object') return [];
            const envelope = value as Partial<CandidateEnvelope>;
            if (typeof envelope.attemptId === 'string') {
                if (envelope.attemptId !== this.activeAttemptId) return [];
                const { attemptId: _attemptId, ...candidate } = envelope;
                return [candidate as RTCIceCandidate];
            }

            // Read candidates written by clients from before attempt versioning.
            return [value as RTCIceCandidate];
        });
        this.iceCandidatesSubject.next(candidates);
    }
}
